import React from 'react';
import { Box, Typography } from '@mui/material';
import { i18n } from '../i18n/i18n';
import { capitalizeFirstLetter, dateRangeFormatter, dateFormatter, formatPhoneNumber, formatUrl } from './formatters';
import {
    IconPlus,
    IconSearch,
    IconInfo,
    IconProfilePicture,
    IconRelationships,
    IconName,
    IconCopy,
    IconCall,
    IconMail,
    IconGender,
    IconSchool,
    IconProffesion,
    IconDateOfBirth,
    IconLocations,
    IconSocialId,
    IconDirectionsCar,
    IconHome,
    IconWatchList,
} from '../images';
import { copyToClipboard } from './communication';
import moment from 'moment/moment';
import { addressesMap } from '../../investigate/Content/routes/collections/Collection/constants';
import { SearchInfo, Source } from '../models/overview';
import {
    ANALYSIS_TYPE,
    connectionTags,
    PLATFORM,
    reactionIcons,
    SEARCH_TYPE,
    socialPlatformsMap,
} from '../constants/constants';
import { UniqueSourcesIconList, CustomActions, CustomList } from '../components';
import _ from 'lodash';
import { FilterOption } from '../components/FiltersPanel/filter-types';
import CustomAvatar from '../components/CustomAvatar/CustomAvatar';
import { Tooltip, ElementsDivider, COLORS } from '@cellebrite/saas-ui-common';
import { PENDO_EVENTS } from './pendo';
import MarkedText from '../components/MarkedText/MarkedText';
import { WatchList } from '../components/CustomInfoCard/infoBlock';
import MultiMarkedText from '../components/MultiMarkedText/MultiMarkedText';

const iconsLib = require('@cellebrite/saas-ui-icons');
export const getPlatformImage = (platform) => {
    const unknown = 'unknown';
    let result = '';

    try {
        result = require(`src/common/images/social-icons/${platform.toLowerCase()}.svg`);
    } catch (e) {
        if (platform) {
            result = require(`src/common/images/social-icons/${unknown}.svg`);
        }
    }

    return result;
};
export const getReactionIconByPlatform = (platform, name) => {
    const unknown = 'unknown';
    let result = '';

    try {
        result = require(`src/common/images/reactions/${platform?.toLowerCase()}/${name?.toLowerCase()}.svg`);
    } catch (e) {
        result = require(`src/common/images/social-icons/${unknown}.svg`);
    }

    return result;
};
export const getSearchTypeFromSource = (searchInfo: SearchInfo) => {
    let type: any;
    if (searchInfo?.isInitialSearch) {
        type = i18n.translate('Initial Search');
    } else if (searchInfo?.isManuallyAddedSearch) {
        type = i18n.translate('Manually Added Search');
    } else if (searchInfo?.isAutomaticEnrichment) {
        type = i18n.translate('Automated Search');
    }
    return type;
};
export const getSourcesAsList = (originalSources: Source[]) => {
    return originalSources?.sort().map((source, index) => {
        const searchType = getSearchTypeFromSource(source.searchInfo);
        const title = source.site || source.source || source.provider;
        const showSource = source.source !== title;
        const sourceIcon = getPlatformImage(source.searchIdentifier.network || source.source);

        return (
            <Box key={index} sx={{ mt: index ? 2 : 0 }}>
                <Typography variant="s16w500">{title}</Typography>
                {searchType && (
                    <Box>
                        <Typography variant="s16w400" className="color-md">
                            {searchType}
                        </Typography>
                    </Box>
                )}
                {source.searchIdentifier && (
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                        <Typography variant="s16w400" className="color-md" sx={{ textWrap: 'nowrap' }}>
                            {i18n.translate('Search Term')}:
                        </Typography>
                        <Typography variant="s16w400" sx={{ ml: 1, mr: 1, textWrap: 'nowrap' }}>
                            {source.searchIdentifier.identifier}
                        </Typography>
                        <img width="16" height="16" src={sourceIcon} alt="" />
                    </Box>
                )}
                {showSource && (
                    <Box sx={{ display: 'flex' }}>
                        <Typography variant="s16w400" className="color-md">
                            {i18n.translate('Source')}:
                        </Typography>
                        <Typography variant="s16w400" sx={{ ml: 1 }}>
                            {source.source}
                        </Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex' }}>
                    <Typography variant="s16w400" className="color-md">
                        {i18n.translate('Provider')}:
                    </Typography>
                    <Typography variant="s16w400" sx={{ ml: 1 }}>
                        {source.provider}
                    </Typography>
                </Box>
                {dateFormatter(source.lastSeen).length > 1 && (
                    <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
                        <Typography variant="s16w400" className="color-md">
                            {i18n.translate('Last Seen')}:
                        </Typography>
                        <Typography variant="s16w400" sx={{ ml: 1 }}>
                            {dateFormatter(source.lastSeen)}
                        </Typography>
                    </Box>
                )}
                {dateFormatter(source.validSince).length > 1 && (
                    <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
                        <Typography variant="s16w400" className="color-md">
                            {i18n.translate('Valid Since')}:
                        </Typography>
                        <Typography variant="s16w400" sx={{ ml: 1 }}>
                            {dateFormatter(source.validSince)}
                        </Typography>
                    </Box>
                )}
                {dateFormatter(source.lastVerified).length > 1 && (
                    <Box sx={{ display: 'flex', whiteSpace: 'nowrap' }}>
                        <Typography variant="s16w400" className="color-md">
                            {i18n.translate('Last Verified')}:
                        </Typography>
                        <Typography variant="s16w400" sx={{ ml: 1 }}>
                            {dateFormatter(source.lastVerified)}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    });
};

export const getAccountsStatusMessage = (isPublic: boolean | null, platform: string) => {
    let resultMessage = {
        title: null,
        explanationText: null,
    };

    if (isPublic === null) return resultMessage;

    if (isPublic) {
        resultMessage.title = i18n.translate(
            platform?.toLowerCase() === 'facebook' ? 'Public Friends List' : 'Account is Public',
        );
    } else {
        resultMessage.title = i18n.translate(
            platform?.toLowerCase() === 'facebook' ? 'Private Friends List' : 'Account is Private',
        );

        resultMessage.explanationText = i18n.translate(
            platform?.toLowerCase() === 'facebook'
                ? 'This accounts friends list was made private and hidden from the general public'
                : 'This account was made private, making its posts, followers list and following list hidden from the general public',
        );
    }

    return resultMessage;
};

export const formatPost = (postRawData) => {
    const newPost = { ...postRawData };
    newPost.url = formatUrl(newPost.url);
    newPost.profileInfo = {};
    newPost.profileInfo.id = postRawData.author?.username || postRawData.author?.userId;
    newPost.profileInfo.name = postRawData.author?.name;
    newPost.profileInfo.imageSrc = postRawData.profileImageUrl;
    newPost.publishDate = postRawData.datePublished?.datePublished;
    newPost.title = 'Post';
    newPost.headLine = postRawData.author.source;
    newPost.platform = postRawData.author.source;
    newPost.media = postRawData.attachments
        .filter((attachment) => attachment.urls?.length)
        .map((attachment) => {
            return { type: attachment.contentType, url: attachment.urls[0] };
        });
    return newPost;
};

interface InfoBlock {
    initialExpanded?: boolean;
    title: string;
    icon: React.ReactNode;
    type?: string;
    watched?: boolean;
    watchLists?: WatchList[];
    items: InfoBlockItem[];
}

interface InfoBlockItem {
    id?: string;
    keywords?: string[];
    tags?: any[];
    value?: any;
    sources?: React.ReactNode;
    itemType?: string;
    additionalInfo?: {
        primaryInfoText?: any;
        secondaryInfoText?: any;
    };
    actions?: any[];
}

function getUniqueWatchLists(data) {
    return _(data)
        .flatMap('watchlist')
        .groupBy('watchlistId')
        .map((group) => ({
            watchlistId: group[0]?.watchlistId,
            watchlistDisplayName: group[0]?.watchlistDisplayName,
            keywords: _.uniq(_.flatMap(group, 'keywords')),
        }))
        .value();
}

export const getKeywordsFromWatchLists = (watchLists, id) => {
    return watchLists
        ?.filter((watchlistItem) => (id ? watchlistItem.watchlistId === id : true))
        .map((watchlistItem) => watchlistItem.keywords)
        .flat(1);
};

export const createInfoBlocks = (
    pendoEventLocation,
    overview,
    createCollection?,
    addToReport?,
    streamId?,
    isQuotaLimitReached?,
    watchlistId?,
) => {
    const infoBlockObj: {
        leftSections: InfoBlock[];
        rightSections: any[];
    } = {
        leftSections: [],
        rightSections: [],
    };
    const getItemType = (searchInput) => {
        if (!searchInput) {
            return null;
        } else if (searchInput.isInitialSearchInput) {
            return 'isInitialSearchInput';
        } else if (searchInput.isAddedSearchInput) {
            return 'isAddedSearchInput';
        } else {
            return null;
        }
    };

    const copyAction = (copiedText: string) => ({
        name: i18n.translate('Copy to Clipboard'),
        icon: <IconCopy />,
        onClick: () => copyToClipboard(copiedText),
    });

    if (overview.profilePictures?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.profilePictures.filter((location) => location.watchlist?.length).length,
            initialExpanded: true,
            title: i18n.translate('Profile Pictures'),
            icon: <IconProfilePicture className="fill-b" />,
            type: 'avatars',
            items: overview.profilePictures.map((item) => ({
                tags: item.tags,
                value: {
                    id: item.value.imageId,
                    source: item.value.imgLocalSrc,
                    platform: item.sources[0].source || item.sources[0].provider,
                },
            })),
        });
    }

    if (overview.names?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.names.filter((location) => location.watchlist?.length).length,
            initialExpanded: true,
            title: i18n.translate('Names'),
            icon: <IconName className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.names),
            items: overview.names.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                itemType: getItemType(item.searchInput),
                additionalInfo: {
                    primaryInfoText: item.value,
                },
                actions: [
                    {
                        name: i18n.translate('Create a New Search'),
                        icon: <IconSearch />,
                        disabled: isQuotaLimitReached,
                        onClick: () => {
                            if (createCollection) {
                                createCollection({
                                    pendoEventName: PENDO_EVENTS[`SEARCH_${pendoEventLocation}_NAMES`],
                                    newSearch: {
                                        searchTerm: item.value,
                                        analysisType: ANALYSIS_TYPE.LOOKUP,
                                        searchType: SEARCH_TYPE.BY_NAME,
                                    },
                                });
                            }
                        },
                    },
                    copyAction(item.value),
                ],
            })),
        });
    }

    if (overview.usernames?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.usernames.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Usernames'),
            icon: <IconSocialId className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.usernames),
            items: overview.usernames.map((item) => ({
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                id: item.identifier,
                tags: item.tags,
                value: '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value?.userName || item.value,
                },
                actions: [copyAction(item.value?.userName || item.value)],
            })),
        });
    }

    if (overview.phones?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.phones.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Phone Numbers'),
            icon: <IconCall className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.phones),
            items: overview.phones.map((item) => {
                const searchTerm = `+${item.value.countryCode ? item.value?.countryCode : ''}${
                    item.value.phone ? item.value?.phone : ''
                }`;
                const actions = [
                    {
                        name: i18n.translate('Create a New Search'),
                        icon: <IconSearch />,
                        disabled: isQuotaLimitReached,
                        onClick: () => {
                            if (createCollection) {
                                createCollection({
                                    pendoEventName: PENDO_EVENTS[`SEARCH_${pendoEventLocation}_PHONES`],
                                    newSearch: {
                                        searchTerm: searchTerm,
                                        analysisType: ANALYSIS_TYPE.FULL,
                                        searchType: 'ByPhoneNumber',
                                    },
                                });
                            }
                        },
                    },
                    copyAction(searchTerm),
                ];
                if (!item.searchInput?.isAddedSearchInput && !item.searchInput?.isInitialSearchInput) {
                    actions.unshift({
                        name: i18n.translate('Add to this Search'),
                        icon: <IconPlus />,
                        disabled: isQuotaLimitReached,
                        onClick: () => {
                            if (addToReport) {
                                addToReport({
                                    pendoEventName: PENDO_EVENTS[`ADD_TO_REPORT_${pendoEventLocation}_PHONES`],
                                    payload: {
                                        searchType: 'ByPhoneNumber',
                                        searchTerm: searchTerm,
                                        displaySearchTerm: searchTerm,
                                        streamId: streamId,
                                    },
                                });
                            }
                        },
                    });
                }
                return {
                    id: item.identifier,
                    keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                    tags: item.tags,
                    value: '',
                    sources: <UniqueSourcesIconList originalSources={item.sources} />,
                    itemType: getItemType(item.searchInput),
                    additionalInfo: {
                        primaryInfoText: formatPhoneNumber(item.value),
                    },
                    actions,
                };
            }),
        });
    }

    if (overview.emails?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.emails.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Emails'),
            icon: <IconMail className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.emails),
            items: overview.emails.map((item) => {
                const actions = [
                    {
                        name: i18n.translate('Create a New Search'),
                        icon: <IconSearch />,
                        disabled: isQuotaLimitReached,
                        onClick: () => {
                            if (createCollection) {
                                createCollection({
                                    pendoEventName: PENDO_EVENTS[`SEARCH_${pendoEventLocation}_EMAILS`],
                                    newSearch: {
                                        searchTerm: item.value,
                                        analysisType: ANALYSIS_TYPE.FULL,
                                        searchType: 'ByEmailAddress',
                                    },
                                });
                            }
                        },
                    },
                    copyAction(item.value),
                ];
                if (!item.searchInput?.isAddedSearchInput && !item.searchInput?.isInitialSearchInput) {
                    actions.unshift({
                        name: i18n.translate('Add to this Search'),
                        icon: <IconPlus />,
                        disabled: isQuotaLimitReached,
                        onClick: () => {
                            if (addToReport) {
                                addToReport({
                                    pendoEventName: PENDO_EVENTS[`ADD_TO_REPORT_${pendoEventLocation}_EMAILS`],
                                    payload: {
                                        searchType: 'ByEmailAddress',
                                        searchTerm: item.value,
                                        displaySearchTerm: item.value,
                                    },
                                });
                            }
                        },
                    });
                }
                return {
                    id: item.identifier,
                    keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                    tags: item.tags,
                    value: '',
                    sources: <UniqueSourcesIconList originalSources={item.sources} />,
                    itemType: getItemType(item.searchInput),
                    additionalInfo: {
                        primaryInfoText: item.value,
                    },
                    actions,
                };
            }),
        });
    }

    if (overview.work?.jobs?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.work.jobs.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Work'),
            icon: <IconProffesion className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.work.jobs),
            items: overview.work.jobs.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: item.value.jobTitle,
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value.company || null,
                    secondaryInfoText: dateRangeFormatter(item.value.start, item.value.end),
                },
                actions: [copyAction(item.value.jobTitle)],
            })),
        });
    }

    if (overview.education?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.education.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Education'),
            icon: <IconSchool className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.education),
            items: overview.education.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: item.value.degree ? item.value.degree : '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value.school,
                    secondaryInfoText: dateRangeFormatter(item.value.start, item.value.end),
                },
                actions: [copyAction(item.value.school)],
            })),
        });
    }

    if (overview.locations?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.locations.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Addresses'),
            icon: <IconLocations className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.locations),
            items: overview.locations.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value?.address,
                    secondaryInfoText: addressesMap.get(item.value?.label?.toUpperCase()),
                },
                actions: [copyAction(item.value.address)],
            })),
        });
    }

    if (overview.relationships?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.relationships.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Relationships'),
            icon: <IconRelationships className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.relationships),
            items: overview.relationships.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: item.value.value,
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value.type,
                },
                actions: [copyAction(item.value?.value || item.value.type)],
            })),
        });
    }

    if (overview.userIds?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.userIds.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('User IDs'),
            icon: <IconSocialId className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.userIds),
            items: overview.userIds.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value?.userId,
                },
                actions: [copyAction(item.value?.userId)],
            })),
        });
    }

    if (overview.dates?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.dates.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Dates'),
            icon: <IconDateOfBirth className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.dates),
            items: overview.dates.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value?.type,
                    secondaryInfoText: dateFormatter(item.value),
                },
                actions: [copyAction(dateFormatter(item.value))],
            })),
        });
    }

    if (overview.homeOwnership?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.homeOwnership.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Home Ownership'),
            icon: <IconHome className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.homeOwnership),
            items: overview.homeOwnership.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: item.value.display,
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: [
                        item.value.classification === 'apn' &&
                            i18n.translate('Home Ownership apn', { value: item.value.content }),
                        (item.value.purchasedOn &&
                            i18n.translate('PURCHASED_ON', {
                                date: dateFormatter(item.value.purchasedOn, 'MMM D, YYYY'),
                            })) ||
                            i18n.translate('LAST_SALE_DATE', { date: dateFormatter(item.value.saleDate) }),
                    ],
                    secondaryInfoText:
                        item.value.livesInProperty && item.value.content
                            ? i18n.translate('Home owner lives in property')
                            : '',
                },
                actions: [copyAction(item.value.display)],
            })),
        });
    }

    if (!overview.dates?.length && overview.dob?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.dob.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Date of Birth'),
            icon: <IconDateOfBirth className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.dob),
            items: overview.dob.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: dateFormatter(item.value),
                },
                actions: [copyAction(dateFormatter(moment(item.value.start).format('LL')))],
            })),
        });
    }

    if (overview.gender) {
        infoBlockObj.leftSections.push({
            watched: !!overview.gender.watchlist?.length,
            title: i18n.translate('Gender'),
            icon: <IconGender className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.gender),
            items: [
                {
                    id: overview.gender.identifier,
                    keywords: getKeywordsFromWatchLists(overview.gender.watchlist, watchlistId),
                    tags: overview.gender.tags,
                    value: '',
                    sources: <UniqueSourcesIconList originalSources={overview.gender.sources} />,
                    additionalInfo: {
                        primaryInfoText: capitalizeFirstLetter(overview.gender.value),
                    },
                    actions: [copyAction(overview.gender.value)],
                },
            ],
        });
    }

    if (overview.vehicles?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.vehicles.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('Vehicles'),
            icon: <IconDirectionsCar className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.vehicles),
            items: overview.vehicles.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value.vin,
                    secondaryInfoText: item.value.display,
                },
                actions: [copyAction(item.value.vin)],
            })),
        });
    }

    if (overview.urls?.length) {
        infoBlockObj.leftSections.push({
            watched: !!overview.urls.filter((location) => location.watchlist?.length).length,
            title: i18n.translate('URLs'),
            icon: <IconSocialId className="fill-b" />,
            watchLists: getUniqueWatchLists(overview.urls),
            items: overview.urls.map((item) => ({
                id: item.identifier,
                keywords: getKeywordsFromWatchLists(item.watchlist, watchlistId),
                tags: item.tags,
                value: '',
                sources: <UniqueSourcesIconList originalSources={item.sources} />,
                additionalInfo: {
                    primaryInfoText: item.value?.url,
                    secondaryInfoText: item.value?.type,
                },
                actions: [copyAction(item.value?.url)],
            })),
        });
    }

    return infoBlockObj;
};

export const getPostActions = (post, containerStyles?, prefixActions?, onToggleActions?, prefixElement?) => {
    const actions = [
        {
            name: i18n.translate('Go to the Post'),
            icon: <IconCopy />,
            disabled: !post.url,
            onClick: (e) => {
                window.open(post.url, '_blank', 'noopener,noreferrer');
                e.stopPropagation();
            },
        },
        {
            name: i18n.translate('Copy Post'),
            icon: <IconCopy />,
            onClick: () =>
                copyToClipboard(
                    `${i18n.translate('Site')}: ${
                        PLATFORM[post.platform.toUpperCase()]
                            ? PLATFORM[post.platform.toUpperCase()]
                            : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('Post ID')}: ${
                        post.contentId ? post.contentId : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('URL')}: ${
                        post.url ? post.url : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('Author Name')}: ${
                        post.author.name ? post.author.name : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('Author User ID')}: ${
                        post.author.userId ? post.author.userId : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('Author Username')}: ${
                        post.author.username ? post.author.username : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('Date Published')}: ${
                        post.publishDate ? dateFormatter(post.publishDate) : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('Body')}: ${post.body ? post.body : i18n.translate('NOT_APPLICABLE')}\n`,
                ),
        },
    ];
    if (prefixActions?.length) {
        prefixActions.forEach((prefixAction) => actions.unshift(prefixAction));
    }
    return (
        <CustomActions
            containerStyles={containerStyles ? containerStyles : {}}
            prefixElement={prefixElement}
            tooltip={{ content: getSourcesAsList([post.source]), icon: <IconInfo />, placement: 'bottom' }}
            actions={actions}
            onToggleActions={onToggleActions}
        />
    );
};

export const getAccountActions = (
    account,
    createCollection?,
    containerStyles?,
    prefixActions?,
    onToggleActions?,
    isQuotaLimitReached?,
    prefixElement?,
) => {
    const url = account.url || account.profileUrl;
    const site = account.site || account.type;
    const actions = [
        {
            name: i18n.translate('Create a New Search'),
            icon: <IconSearch />,
            disabled: isQuotaLimitReached,
            onClick: () => {
                if (createCollection) {
                    createCollection({
                        pendoEventName: PENDO_EVENTS.SEARCH_REPORT_CONNECTION_OPTION,
                        newSearch: {
                            searchTerm: account.userName || account.userId,
                            searchType: socialPlatformsMap.get(capitalizeFirstLetter(site)),
                            analysisType: ANALYSIS_TYPE.SOCIAL,
                        },
                    });
                }
            },
        },
        {
            name: i18n.translate('Go to the Profile'),
            icon: <IconCopy />,
            disabled: !url,
            onClick: () => window.open(url, '_blank', 'noopener,noreferrer'),
        },
        {
            name: i18n.translate('Copy Account'),
            icon: <IconCopy />,
            onClick: () =>
                copyToClipboard(
                    `${i18n.translate('Site')}: ${
                        PLATFORM[site.toUpperCase()] ? PLATFORM[site.toUpperCase()] : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('Name')}: ${
                        account.name ? account.name : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('Username')}: ${
                        account.userName ? account.userName : i18n.translate('NOT_APPLICABLE')
                    }\n${i18n.translate('ID')}: ${account.userId}\n${i18n.translate('URL')}: ${
                        url ? url : i18n.translate('NOT_APPLICABLE')
                    }\n`,
                ),
        },
    ];
    if (prefixActions?.length) {
        prefixActions.forEach((prefixAction) => actions.unshift(prefixAction));
    }
    return (
        <CustomActions
            prefixElement={prefixElement}
            containerStyles={containerStyles ? containerStyles : {}}
            tooltip={{ content: getSourcesAsList(account.sources), icon: <IconInfo />, placement: 'bottom' }}
            actions={actions}
            onToggleActions={onToggleActions}
        />
    );
};

export const formatDateOfBirth = (dob) => {
    const startDate = moment(dob?.start);
    const endDate = moment(dob?.end);

    let result = startDate.format('MMM, YYYY');
    if (endDate.diff(startDate, 'months', true) > 1) {
        result = startDate.format('YYYY');
    } else if (dob?.value?.start === dob?.value?.end) {
        result = startDate.format('MMM D, YYYY');
    }
    return result;
};

export const getConnectionSections = (account, tags, find, displayHighlights?, keywords?) => {
    const connectionInteractions = getConnectionInteractions(account, find);
    const detailsRows = [
        {
            label: i18n.translate('Account Details'),
            element: (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {account.userName && (
                        <Box sx={{ display: 'flex' }}>
                            <Typography
                                sx={{ whiteSpace: 'nowrap' }}
                                variant="s16w400"
                                className="color-md"
                            >{`${i18n.translate('Username')}:`}</Typography>
                            {displayHighlights ? (
                                <MultiMarkedText
                                    containerProps={{
                                        variant: 's16w400',
                                        sx: {
                                            'ml': 2,
                                            'whiteSpace': 'normal',
                                            '& mark': {
                                                backgroundColor: `${COLORS.NEGATIVE} !important`,
                                                color: `${COLORS.LIGHT} !important`,
                                            },
                                        },
                                    }}
                                    searchTextArr={keywords}
                                >
                                    {account.userName}
                                </MultiMarkedText>
                            ) : (
                                <MarkedText
                                    containerProps={{
                                        variant: 's16w400',
                                        sx: {
                                            'ml': 2,
                                            'whiteSpace': 'normal',
                                            '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                        },
                                    }}
                                    searchText={find}
                                >
                                    {account.userName}
                                </MarkedText>
                            )}
                        </Box>
                    )}
                    {account.userId && (
                        <Box sx={{ display: 'flex' }}>
                            <Typography
                                sx={{ whiteSpace: 'nowrap' }}
                                variant="s16w400"
                                className="color-md"
                            >{`${i18n.translate('User ID')}:`}</Typography>
                            {displayHighlights ? (
                                <MultiMarkedText
                                    containerProps={{
                                        title: account.userId,
                                        variant: 's16w400',
                                        sx: {
                                            'ml': 2,
                                            'whiteSpace': 'normal',
                                            '& mark': {
                                                backgroundColor: `${COLORS.NEGATIVE} !important`,
                                                color: `${COLORS.LIGHT} !important`,
                                            },
                                        },
                                    }}
                                    searchTextArr={keywords}
                                >
                                    {account.userId}
                                </MultiMarkedText>
                            ) : (
                                <MarkedText
                                    containerProps={{
                                        title: account.userId,
                                        variant: 's16w400',
                                        sx: {
                                            'ml': 2,
                                            'whiteSpace': 'normal',
                                            '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                        },
                                    }}
                                    searchText={find}
                                >
                                    {account.userId}
                                </MarkedText>
                            )}
                        </Box>
                    )}
                    {account.formattedDateCreated && (
                        <MarkedText
                            containerProps={{
                                className: 'color-md',
                                sx: { '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` } },
                            }}
                            searchText={find}
                        >
                            {`${i18n.translate('Since')} ${dateFormatter(account.formattedDateCreated)}`}
                        </MarkedText>
                    )}
                </Box>
            ),
        },
    ];
    if (account.aboutText) {
        detailsRows.push({
            label: i18n.translate('About'),
            element: displayHighlights ? (
                <MultiMarkedText
                    containerProps={{
                        variant: 's16w400',
                        sx: {
                            'whiteSpace': 'normal',
                            '& mark': {
                                backgroundColor: `${COLORS.NEGATIVE} !important`,
                                color: `${COLORS.LIGHT} !important`,
                            },
                        },
                    }}
                    searchTextArr={keywords}
                >
                    {account.aboutText}
                </MultiMarkedText>
            ) : (
                <MarkedText
                    containerProps={{
                        variant: 's16w400',
                        sx: {
                            'whiteSpace': 'normal',
                            '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                        },
                    }}
                    searchText={find}
                >
                    {account.aboutText}
                </MarkedText>
            ),
        });
    }
    if (account.profileLocation) {
        detailsRows.push({
            label: i18n.translate('Location'),
            element: (
                <MarkedText
                    containerProps={{
                        variant: 's16w400',
                        sx: {
                            'whiteSpace': 'normal',
                            '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                        },
                    }}
                    searchText={find}
                >
                    {account.profileLocation}
                </MarkedText>
            ),
        });
    }
    const sections = [
        {
            rows: detailsRows,
        },
    ];
    if (connectionInteractions.length) {
        const commonRows = [
            {
                label: i18n.translate('Interactions'),
                element: <ElementsDivider dividerOverrides={{ height: 16 }} elements={connectionInteractions} />,
            },
        ];
        if (tags?.length) {
            commonRows.push({
                label: i18n.translate('Tags'),
                element: (
                    <CustomList
                        gap={{ width: 2 }}
                        list={tags.map((tag) => (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: tag.color,
                                        borderRadius: 2,
                                        mr: 1,
                                    }}
                                ></Box>
                                <Typography variant="s14w500">{tag.name}</Typography>
                            </Box>
                        ))}
                    />
                ),
            });
        }
        sections.push({
            title: i18n.translate('In Common with the Person of Interest'),
            rows: commonRows,
            separator: true,
            titleTypeSecondary: true,
        } as any);
    } else {
        if (tags?.length) {
            detailsRows.push({
                label: i18n.translate('Tags'),
                element: (
                    <CustomList
                        gap={{ width: 2 }}
                        list={tags.map((tag) => (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: tag.color,
                                        borderRadius: 2,
                                        mr: 1,
                                    }}
                                ></Box>
                                <Typography variant="s14w500">{tag.name}</Typography>
                            </Box>
                        ))}
                    />
                ),
            });
        }
    }
    return sections;
};

export const getConnectionInteractions = (account, find) => {
    const result = [];

    account.interactions?.singular.forEach((interaction) =>
        result.push(
            <MarkedText
                containerProps={{
                    variant: 's16w400',
                    sx: { '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` } },
                }}
                searchText={find}
            >
                {i18n.translate(connectionTags[interaction.type])}
            </MarkedText>,
        ),
    );

    account.interactions?.plural.forEach((interaction) => {
        if (interaction.type === 'REACTED' && interaction.reactions) {
            result.push(
                <Tooltip
                    styles={{ p: 0 }}
                    placement="right"
                    content={
                        <Box sx={{ minWidth: 44 }}>
                            {interaction.reactions && (
                                <>
                                    {Object.entries(interaction.reactions).map(([reaction, reactionCount]) => {
                                        const Icon = reactionIcons[reaction] || reactionIcons['like'];
                                        return (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                                key={reaction}
                                            >
                                                <Icon />{' '}
                                                <Typography variant="s16w500">{reactionCount as number}</Typography>
                                            </Box>
                                        );
                                    })}
                                </>
                            )}
                        </Box>
                    }
                    children={
                        <MarkedText
                            containerProps={{
                                variant: 's16w400',
                                sx: { '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` } },
                            }}
                            searchText={find}
                        >
                            {`${interaction.count} ${i18n.translate(connectionTags[interaction.type] || interaction.type)}`}
                        </MarkedText>
                    }
                />,
            );
        } else {
            result.push(
                <MarkedText
                    containerProps={{
                        variant: 's16w400',
                        sx: { '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` } },
                    }}
                    searchText={find}
                >
                    {`${interaction.count} ${i18n.translate(connectionTags[interaction.type])}`}
                </MarkedText>,
            );
        }
    });

    return result;
};
export const getIcon = (iconName) => {
    let src;
    const iconNamePlural = `${iconName}sIcon`;
    iconName = `${iconName}Icon`;

    try {
        if (iconsLib[iconName]) {
            src = iconsLib[iconName];
        } else if (iconsLib[iconNamePlural]) {
            src = iconsLib[iconNamePlural];
        } else {
            src = iconsLib.UnknownIcon;
        }
    } catch (e) {
        src = iconsLib.UnknownIcon;
    }
    return src;
};

export const getIconNameFromCategoryName = (name: string) => {
    if (name === 'Hashish') {
        name = 'Drugs';
    } else if (name === 'Barcode Qr Code') {
        name = 'Qr Codes';
    }
    return name?.replaceAll(/\s/g, '');
};

export const getSortedReactions = (post: any) => {
    const sortedReactions = [];
    if (post.reactionsCountData) {
        _.map(post.reactionsCountData, (reaction: any) => {
            if (reaction.reactionType !== 'reactions') {
                sortedReactions.push({
                    type: reaction.reactionType,
                    collected: reaction.collected,
                    total: reaction.total,
                    count: reaction.count,
                });
            }
        });
    } else {
        _.forOwn(post.reactionsCounts, (count: any, type) => {
            sortedReactions.push({ type, collected: count.collected, total: count.total, count: count });
        });
    }
    return _.orderBy(sortedReactions, ['type'], ['asc']);
};

export const getSourceFromReaction = (platform: string, reactionType: string) => {
    let src;
    try {
        src = require(`src/common/images/reactions/${platform.toLowerCase()}/${reactionType.toLowerCase()}.svg`);
    } catch (e) {
        const unknown = 'unknown';
        src = require(`src/common/images/social-icons/${unknown}.svg`);
    }
    return src;
};

export const getSourceFromPlatform = (platform: string) => {
    let src;
    try {
        src = require(`src/common/images/social-icons/${platform.toLowerCase()}.svg`);
    } catch (e) {
        const unknown = 'unknown';
        src = require(`src/common/images/social-icons/${unknown}.svg`);
    }
    return src;
};
export const getPostReactionsTabsInfo = (post, postReactions, displayHighlights, currentHighlightsWatchlist) => {
    function getContent(type?) {
        const commentsKeywords = getKeywordsFromWatchLists(post?.watchlist, currentHighlightsWatchlist?.id);
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
                {postReactions
                    ?.filter((reaction) => !type || reaction.reactionType?.toLowerCase() === type?.toLowerCase())
                    .map((reaction) => (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CustomAvatar
                                avatar={{
                                    source: getSourceFromReaction(post.platform, reaction.reactionType),
                                    imgLocalSrc: reaction.author?.profileImageUrl,
                                    badgeDirectSrc: true,
                                }}
                            />
                            {displayHighlights ? (
                                <MultiMarkedText
                                    containerProps={{
                                        className: 'color-mh',
                                        variant: 's14w500',
                                        sx: {
                                            'wordBreak': 'break-word',
                                            '& mark': {
                                                backgroundColor: `${COLORS.NEGATIVE} !important`,
                                                color: `${COLORS.LIGHT} !important`,
                                            },
                                        },
                                    }}
                                    searchTextArr={commentsKeywords}
                                >
                                    {reaction.author?.name}
                                </MultiMarkedText>
                            ) : (
                                <Typography sx={{ ml: 1 }} variant="s16w500" className="color-mh">
                                    {reaction.author?.name}
                                </Typography>
                            )}
                        </Box>
                    ))}
            </Box>
        );
    }
    const result = getSortedReactions(post).map((reaction, index) => {
        const src = getSourceFromReaction(post.platform, reaction.type);
        let label = null;
        if (src) {
            label = (
                <Box sx={{ display: 'flex' }}>
                    <img src={src} style={{ zIndex: index, marginLeft: index ? '-3px' : null }} key={index} />
                    <Typography variant="s14w500" sx={{ ml: 2 }}>
                        {reaction.count ? reaction.count : `${reaction.collected}/${reaction.total}`}
                    </Typography>
                </Box>
            );
        }
        return { label: label, content: getContent(reaction.type) };
    });
    const allReactions = _.find(post.reactionsCountData, { reactionType: 'reactions' });
    result.unshift({
        label: (
            <Box sx={{ display: 'flex', textTransform: 'none' }}>
                <Typography variant="s14w500">{i18n.translate('All')}</Typography>
                {allReactions && (
                    <Typography variant="s14w500" sx={{ ml: 1 }}>
                        {`${allReactions?.collected}/${allReactions?.total}`}
                    </Typography>
                )}
            </Box>
        ),
        content: getContent(),
    });
    return result;
};

export const mapFiltersToExport = (filtersToApply: object): object => {
    const result = {};
    _.forOwn(filtersToApply, (value: FilterOption[] | FilterOption, key) => {
        if (_.isArray(value)) {
            result[key] = value?.map((item) => {
                return item.value;
            });
        } else {
            result[key] = value?.value;
        }
    });
    return result;
};

export const fileToByteArray = (file: File): Promise<Uint8Array> => {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject('No file provided');
            return;
        }
        const reader = new FileReader();
        reader.onload = function (event) {
            if (!event.target) {
                reject('Failed to read file');
                return;
            }
            const arrayBuffer = event.target.result;
            const byteArray = new Uint8Array(arrayBuffer as ArrayBuffer);
            resolve(byteArray);
        };
        reader.onerror = function (error) {
            reject(error);
        };
        reader.readAsArrayBuffer(file);
    });
};

export const getWatchListIcon = () => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '4px',
                backgroundColor: COLORS.MUTED_SOFT,
                fill: '#FF6D6D',
            }}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <IconWatchList />
        </Box>
    );
};

export const multiMarking = (id, section, type, link) => {
    const obj = {};

    if (link) {
        obj[`& mark:nth-of-type(${link.index + 1})`] =
            id === link.id && type === link.type && link.section === section
                ? { backgroundColor: `${COLORS.NEGATIVE} !important`, color: `${COLORS.LIGHT} !important` }
                : null;
    }
    return obj;
};
